import { useEffect, useState } from 'react';
import { Target } from 'lucide-react';
import { fetchAdminChallenges, type AdminChallenge } from '@/lib/api';
import { useRecentRoute } from '@/hooks/useRecentRoute';
import styles from './AdminList.module.css';

const STATUS_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'completed', label: 'Completed' },
  { value: 'expired', label: 'Expired' },
];

function statusClass(status: string): string {
  if (status === 'completed') return styles.badgeOk!;
  if (status === 'active') return styles.badgePending!;
  return styles.badgeMuted!;
}

export function ChallengesPage() {
  useRecentRoute('Challenges');
  const [rows, setRows] = useState<AdminChallenge[]>([]);
  const [status, setStatus] = useState('active');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    setLoading(true); setError('');
    fetchAdminChallenges({ ...(status ? { status } : {}) })
      .then(setRows)
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load'))
      .finally(() => setLoading(false));
  }, [status]);

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <div className={styles.headerIcon}><Target size={18} /></div>
          <div>
            <h1 className={styles.title}>Challenges</h1>
            <p className={styles.subtitle}>
              {rows.length} challenge row{rows.length === 1 ? '' : 's'}
              {status ? ` · ${status}` : ''}
            </p>
          </div>
        </div>
      </div>

      <div className={styles.filterRow}>
        <select className={styles.select} value={status} onChange={(e) => setStatus(e.target.value)}>
          {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>

      {error && <div className={styles.error}>{error}</div>}
      {loading && <p className={styles.loading}>Loading…</p>}

      {!loading && rows.length === 0 && (
        <div className={styles.empty}>
          <p className={styles.emptyTitle}>No challenges match</p>
        </div>
      )}

      {!loading && rows.length > 0 && (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>User</th>
                <th>Challenge</th>
                <th>Progress</th>
                <th>Week</th>
                <th>Status</th>
                <th>Reward</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td>{r.userName}</td>
                  <td><code className={styles.cardCode}>{r.challengeCode}</code></td>
                  <td>{r.currentValue.toFixed(1)}</td>
                  <td>{new Date(r.weekStart).toLocaleDateString()}</td>
                  <td>
                    <span className={`${styles.badge} ${statusClass(r.status)}`}>
                      {r.status}
                    </span>
                  </td>
                  <td>{r.rewardAwarded ? 'Awarded' : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
